import {DataTableColumn} from "mantine-datatable";
import {Button, Group} from '@mantine/core';
import {Meter} from "../types.ts";
import {I18n} from "@aws-amplify/core";
import {StatusBadge} from "../profile/StatusBadge.tsx";
import {formatTimestamp} from "../../../utils/TableTimestamps.tsx";
import {activate, retire} from '../services/MetersService.ts';
import {useQueryClient} from '@tanstack/react-query';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';

export const useTableColumns = () => {
    const queryClient = useQueryClient();

    // FNP-665: activate a DRAFT meter via the narrow activateMeter mutation (NOT
    // updateMeter, which silently no-ops for catalog/deploy-hook-created meters).
    // Activation is the deliberate step that lets FNP-589 provision the Stripe meter.
    const handleActivate = async (id: string) => {
        await activate(id);
        queryClient.invalidateQueries({queryKey: ['meters']});
    };

    // FNP-748: retire an ACTIVE/DEPRECATED meter via the narrow retireMeter mutation.
    // The billing-api resolver fails closed if the meter still has OPEN metering periods
    // (retiring would de-provision the Stripe meter while usage is still aggregating — a
    // money-path data-loss window). On that block it throws MeterHasOpenPeriods; surface
    // the message so the admin sees WHY the retire was refused.
    const handleRetire = async (id: string) => {
        try {
            await retire(id);
            queryClient.invalidateQueries({queryKey: ['meters']});
            notifySuccess('Meter retired');
        } catch (e) {
            notifyError(e);
        }
    };

    const columns: DataTableColumn<Meter>[] = [
        {
            accessor: 'name',
            title: I18n.get('Meter'),
            width: '1fr',
        },
        {
            accessor: 'meterCode',
            title: I18n.get('Code'),
            width: '1fr',
            render: (item: Meter) => item.meterCode,
        },
        {
            accessor: 'aggregation',
            title: I18n.get('Aggregation'),
            width: '1fr',
            render: (item: Meter) => item.aggregation,
        },
        {
            accessor: 'grain',
            title: I18n.get('Grain'),
            width: '1fr',
            render: (item: Meter) => item.grain,
        },
        {
            accessor: 'status',
            title: I18n.get('Status'),
            width: '1fr',
            render: (item: Meter) => <StatusBadge status={item.status}/>,
        },
        {
            accessor: 'dimensions',
            title: I18n.get('Dimensions'),
            width: '1fr',
            render: (item: Meter) => item.dimensions ? item.dimensions.length : '-',
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Meter) => formatTimestamp(item.created as string),
        },
        {
            accessor: 'actions',
            title: I18n.get('Actions'),
            width: '1fr',
            // FNP-665: an Activate button on DRAFT meters (mirrors the customer-subscriptions
            // activate pattern). Only DRAFT meters show it; activation triggers FNP-589 to
            // mint the Stripe meter on its next reconcile pass.
            // FNP-748: a Retire button on ACTIVE/DEPRECATED meters (the terminal transition).
            // Blocked server-side if the meter has open periods (message surfaced on error).
            render: (item: Meter) => (
                <Group gap="xs">
                    {item.status === 'DRAFT' && (
                        <Button size="xs" onClick={() => handleActivate(item.id)}>
                            {I18n.get('Activate')}
                        </Button>
                    )}
                    {(item.status === 'ACTIVE' || item.status === 'DEPRECATED') && (
                        <Button size="xs" color="red" variant="outline" onClick={() => handleRetire(item.id)}>
                            {I18n.get('Retire')}
                        </Button>
                    )}
                </Group>
            ),
        },

    ];

    return {
        columns,
    }
}
